import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { helpers, useFetch } from '@reveni-io/reveni-ui'

import AppContext from '@/context/AppContext'
import { hasEvaluationOptOut } from '@/helpers/evaluationOptOut/evaluationOptOut'
import { identifyLogRocket, initLogRocket } from '@/helpers/logrocket'
import { initSeon } from '@/helpers/seon/fingerprint'
import routes from '@/Router/routes.constants'
import services from '@/services/services'

import useCancellationsPortal from '../useCancellationsPortal/useCancellationsPortal'
import useOrderError from '../useOrderError/useOrderError'
import { getCheckOrderPayload, getIsReturnableReason } from './helpers'

const useCheckOrder = ({ isGiftReturn, giftRecipient }) => {
  const navigate = useNavigate()
  const { context, setContext } = useContext(AppContext)
  const { loading: orderLoading, fetchData: fetchOrder } = useFetch(services.getOrder, { doInitialRequest: false })
  const { isOrderReturnable, showOrderErrorModal, setShowOrderErrorModal } = useOrderError()
  const { portalType } = useCancellationsPortal()
  const [type, setType] = useState()
  const [navigateToProductsSelectionPage, setNavigateToProductsSelectionPage] = useState(false)

  useEffect(() => {
    if (context.orderInfo.id && navigateToProductsSelectionPage) {
      navigate(routes.productSelectionPage)
    }
  })

  const saveOrderInContext = (data, transactionType) => {
    setContext(context => ({
      ...context,
      displayMessages: data.display_messages || [],
      consentModal: data.consent_modal,
      consentModalAccepted: false,
      orderInfo: {
        ...context.orderInfo,
        ...data,
        type: transactionType,
        is_gift_return: isGiftReturn,
        shipping_address: helpers.formatUserAddress(data.shipping_address, data.customer_profile?.phone),
        line_items: helpers.joinLineItemsAndLineItemGroups(
          helpers.flattenLineItems(data?.line_items, portalType),
          data?.line_item_groups,
          portalType
        ),
      },
      variants: helpers.getLineItemsVariants(data?.line_items, data?.line_item_groups),
    }))
  }

  const initAndIdentifyExternalTools = email => {
    initLogRocket(true)

    if (email) identifyLogRocket(true, email)
  }

  const initSeonUnlessOptedOut = optsOutOfEvaluation => {
    if (!optsOutOfEvaluation) initSeon()
  }

  const checkOrder = async ({ orderNumber, identifier, type: transactionType }) => {
    setType(transactionType)
    initAndIdentifyExternalTools(identifier)

    try {
      const optsOutOfEvaluation = hasEvaluationOptOut()
      const payload = getCheckOrderPayload({
        orderNumber,
        returnPortalId: context.merchantInfo.returnPortalId,
        identifier,
        termsAndConditions: true,
        type: transactionType,
        isGiftReturn,
        giftRecipient,
        optsOutOfEvaluation,
      })

      const data = await fetchOrder(payload)
      if (data) {
        saveOrderInContext(data, transactionType)

        if (
          isOrderReturnable({
            availableTransactionTypesForOrder: data.available_transaction_types,
            lineItems: data?.line_items,
            transactionTypeSelectedByUser: transactionType,
            lineItemGroups: data?.line_item_groups,
          })
        ) {
          initSeonUnlessOptedOut(optsOutOfEvaluation)

          setNavigateToProductsSelectionPage(true)
        } else {
          setShowOrderErrorModal(true)
        }
      }
    } catch (error) {
      setContext(context => ({
        ...context,
        orderInfo: {
          ...context.orderInfo,
          is_returnable_reason: getIsReturnableReason(error),
        },
      }))

      setShowOrderErrorModal(true)
    }
  }

  const closeOrderErrorModal = () => setShowOrderErrorModal(false)

  return { checkOrder, orderLoading, type, showOrderErrorModal, closeOrderErrorModal }
}

export default useCheckOrder
